import React from 'react';

/**
 * Rendered by ErrorBoundary and the top-level Sentry boundary in index.tsx
 * when `chunkRecovery.requestReload()` has exhausted its reload budget for
 * the current incident (see chunkRecovery.ts). This means chunks are still
 * failing to load after automatic retries — most likely a genuinely broken
 * or partial deploy rather than a simple stale-cache race — so we stop
 * looping and ask the user to refresh manually instead.
 *
 * `fullScreen` is ONLY appropriate for the single top-level boundary that
 * wraps the entire app in index.tsx (the whole tab really is broken).
 * Every other caller (ErrorBoundary's 'window'/'panel'/'card'/'inline'
 * variants) is scoped to a content area inside the app — e.g. a single
 * floating desktop Window pane — and must fill that container (height:
 * 100%), not the browser viewport, or it overflows its surroundings.
 */
export const NewVersionAvailable: React.FC<{ fullScreen?: boolean }> = ({ fullScreen = false }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: fullScreen ? '100vh' : '100%',
        gap: '12px',
        padding: '20px',
        textAlign: 'center',
        background: fullScreen ? 'var(--bg-primary, #0f0f0f)' : undefined,
        color: 'var(--text-primary, #e5e5e5)',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
      }}
    >
      <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>A new version of River is available</p>
      <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-tertiary, #6b7280)', maxWidth: '380px' }}>
        Refresh the page to load the latest version.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: '4px',
          padding: '6px 16px',
          fontSize: '13px',
          cursor: 'pointer',
          borderRadius: '6px',
          border: '1px solid var(--border-primary, #2a2a2a)',
          background: 'var(--bg-secondary, #1a1a1a)',
          color: 'var(--text-primary, #e5e5e5)',
        }}
      >
        Refresh
      </button>
    </div>
  );
};
